/**
 * One gamepad event stream for every platform (`03-client-app.md` section 3.1).
 *
 * Desktop gets events from the native `gilrs` plugin, which works even when the window is not
 * focused and knows the controller type. Web and Android poll the browser Gamepad API. Both are
 * normalised to the same buttons here, so nothing above this file knows which one it is using.
 *
 * The feel of a stick lives here too, and it is three rules: one direction at a time, a stick
 * that has to fall well back before it lets go, and a repeat that speeds up the longer it is
 * held. The same three are implemented in the native plugin so the two paths feel identical.
 */

import { isDesktop } from '../platform/index.js';
import { onNative, type GamepadInputEvent } from '../platform/tauri.js';

export type GamepadButton =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'a'
  | 'b'
  | 'x'
  | 'y'
  | 'lb'
  | 'rb'
  | 'lt'
  | 'rt'
  | 'start'
  | 'select';

export type ControllerType = 'xbox' | 'playstation' | 'steamdeck' | 'generic';

export interface GamepadEvent {
  button: GamepadButton;
  controller: ControllerType;
  /** True when the press came from holding the button rather than pushing it again. */
  repeat: boolean;
}

/**
 * How far a stick has to go before it counts, and how far back it has to come before it stops.
 *
 * Two numbers rather than one. With a single threshold, a stick parked on it flickers either
 * side of the mark from the pad's own noise, and every flicker is a fresh press: focus walks
 * across the screen on its own while the hand is holding still. The gap between these two is
 * the room that noise has to live in.
 */
export const STICK_ENGAGE = 0.55;
export const STICK_RELEASE = 0.32;

/**
 * A held direction waits this long, then repeats at this rate, each repeat coming a little
 * sooner than the last until it hits the floor.
 *
 * A flat rate has to choose between being slow enough to stop on one tile and fast enough to
 * cross forty of them, and it cannot be both. Ramping means the first few moves are deliberate
 * and the twentieth is quick, which is what a hand already expects from every other list it has
 * ever held a direction on.
 */
export const REPEAT_DELAY_MS = 380;
export const REPEAT_INTERVAL_MS = 190;
export const REPEAT_FASTEST_MS = 70;
const REPEAT_RAMP = 0.82;

/** The right stick ignores this much, then answers on a curve rather than a straight line. */
const SCROLL_DEADZONE = 0.2;
/** Pixels a full push scrolls per 60 Hz frame. */
const SCROLL_SPEED = 26;
const FRAME_MS = 1000 / 60;

type Listener = (event: GamepadEvent) => void;
type ScrollListener = (delta: number) => void;

const listeners = new Set<Listener>();
const releaseListeners = new Set<Listener>();
const scrollListeners = new Set<ScrollListener>();

let controllerType: ControllerType = 'generic';
let started = false;
let stopNative: (() => void) | null = null;
let rafHandle: number | null = null;

/** Presses, and the repeats of a held direction. */
export function onGamepad(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Releases.
 *
 * Separate from presses because almost nothing cares. A release only matters to the code that
 * has to tell something else a button is no longer down, which is the spatial navigation
 * library and nobody else.
 */
export function onGamepadRelease(listener: Listener): () => void {
  releaseListeners.add(listener);
  return () => releaseListeners.delete(listener);
}

/** Right stick vertical, used to scroll the focused scroll area. */
export function onGamepadScroll(listener: ScrollListener): () => void {
  scrollListeners.add(listener);
  return () => scrollListeners.delete(listener);
}

export function currentControllerType(): ControllerType {
  return controllerType;
}

function emit(button: GamepadButton, repeat: boolean): void {
  const event: GamepadEvent = { button, controller: controllerType, repeat };
  for (const listener of listeners) listener(event);
}

function emitRelease(button: GamepadButton): void {
  const event: GamepadEvent = { button, controller: controllerType, repeat: false };
  for (const listener of releaseListeners) listener(event);
}

/**
 * How far the right stick should scroll this frame.
 *
 * Rescaled from the edge of the deadzone rather than from zero, so the first millimetre past it
 * is a crawl and not a jump, then squared so a small push reads a paragraph and a full push
 * covers a screen. Scaled by the frame time as well: without that the same push scrolls twice
 * as fast on a 120 Hz panel as it does on a 60 Hz one.
 */
function scrollAmount(value: number, frameMs: number): number {
  const magnitude = Math.abs(value);
  if (magnitude < SCROLL_DEADZONE) return 0;
  const t = (magnitude - SCROLL_DEADZONE) / (1 - SCROLL_DEADZONE);
  return Math.sign(value) * t * t * SCROLL_SPEED * (frameMs / FRAME_MS);
}

/* ------------------------------------------------------------- browser API */

/** Standard gamepad mapping button indexes. */
const BUTTON_INDEX: Record<number, GamepadButton> = {
  0: 'a',
  1: 'b',
  2: 'x',
  3: 'y',
  4: 'lb',
  5: 'rb',
  6: 'lt',
  7: 'rt',
  8: 'select',
  9: 'start',
  12: 'up',
  13: 'down',
  14: 'left',
  15: 'right',
};

/** The d-pad, in the order a diagonal press is resolved. */
const DPAD: ReadonlyArray<readonly [number, GamepadButton]> = [
  [12, 'up'],
  [13, 'down'],
  [14, 'left'],
  [15, 'right'],
];

const DIRECTIONS: readonly GamepadButton[] = ['up', 'down', 'left', 'right'];

interface HeldState {
  since: number;
  lastRepeat: number;
  /** The gap until this button's next repeat, which shrinks the longer it is held. */
  gap: number;
}

const held = new Map<GamepadButton, HeldState>();
const REPEATABLE: readonly GamepadButton[] = ['up', 'down', 'left', 'right', 'lt', 'rt'];

/** The direction the stick is being read as, so the release threshold has a subject. */
let stickHeld: GamepadButton | null = null;
let lastPoll = 0;

function detectFromId(id: string): ControllerType {
  const lower = id.toLowerCase();
  if (lower.includes('054c') || lower.includes('dualsense') || lower.includes('dualshock')) {
    return 'playstation';
  }
  if (lower.includes('28de') || lower.includes('steam')) return 'steamdeck';
  if (lower.includes('045e') || lower.includes('xbox')) return 'xbox';
  return 'generic';
}

/**
 * The one direction a stick is pointing, or none.
 *
 * A stick pushed at 45 degrees is pushing up and left at once. Taking both moves focus twice, in
 * two directions, for one push of a thumb — which is most of what "the navigation feels wrong"
 * turns out to be. So the bigger axis wins and the other is dropped; and a direction already
 * being held keeps it until its own axis falls back, rather than being stolen by a neighbour the
 * hand only brushed past on the way.
 */
function stickDirection(x: number, y: number, current: GamepadButton | null): GamepadButton | null {
  const horizontal = Math.abs(x) > Math.abs(y);
  const dominant: GamepadButton = horizontal ? (x < 0 ? 'left' : 'right') : y < 0 ? 'up' : 'down';
  const magnitude = horizontal ? Math.abs(x) : Math.abs(y);

  if (magnitude >= STICK_ENGAGE) return dominant;
  if (current) {
    const alive = current === 'left' || current === 'right' ? Math.abs(x) : Math.abs(y);
    if (alive >= STICK_RELEASE) return current;
  }
  return null;
}

function press(button: GamepadButton, now: number): void {
  const state = held.get(button);
  if (!state) {
    held.set(button, { since: now, lastRepeat: now, gap: REPEAT_INTERVAL_MS });
    emit(button, false);
    return;
  }
  if (!REPEATABLE.includes(button)) return;
  if (now - state.since < REPEAT_DELAY_MS) return;
  if (now - state.lastRepeat < state.gap) return;
  state.lastRepeat = now;
  state.gap = Math.max(REPEAT_FASTEST_MS, state.gap * REPEAT_RAMP);
  emit(button, true);
}

function release(button: GamepadButton): void {
  if (!held.delete(button)) return;
  emitRelease(button);
}

function releaseAll(): void {
  for (const button of [...held.keys()]) release(button);
  stickHeld = null;
}

function poll(): void {
  const pads = navigator.getGamepads?.() ?? [];
  const now = performance.now();
  // Capped, so a tab that was in the background does not come back and scroll a mile.
  const frameMs = lastPoll ? Math.min(now - lastPoll, 50) : FRAME_MS;
  lastPoll = now;

  // Every connected pad is folded into one before anything is decided. Windows very often
  // reports a phantom second controller alongside the real one, and reading each pad in turn
  // would let the idle one release, on its own frame, everything the real one is holding — which
  // reads as input that drops out at random and is impossible to guess at from the outside.
  const down = new Set<GamepadButton>();
  let dpad: GamepadButton | null = null;
  let lx = 0;
  let ly = 0;
  let ry = 0;
  let firstPad: string | null = null;
  let activePad: string | null = null;

  for (const pad of pads) {
    if (!pad) continue;
    firstPad ??= pad.id;

    for (const [index, button] of Object.entries(BUTTON_INDEX)) {
      if (!pad.buttons[Number(index)]?.pressed) continue;
      down.add(button);
      activePad ??= pad.id;
    }
    dpad ??= DPAD.find(([index]) => pad.buttons[index]?.pressed)?.[1] ?? null;

    // The furthest-pushed pad wins each axis, which with one pad is that pad.
    const [px = 0, py = 0, , pr = 0] = pad.axes;
    if (Math.abs(px) > Math.abs(lx)) lx = px;
    if (Math.abs(py) > Math.abs(ly)) ly = py;
    if (Math.abs(pr) > Math.abs(ry)) ry = pr;
    if (Math.abs(px) > STICK_ENGAGE || Math.abs(py) > STICK_ENGAGE) activePad ??= pad.id;
  }

  if (firstPad === null) {
    releaseAll();
    rafHandle = requestAnimationFrame(poll);
    return;
  }

  // The glyphs follow the pad being used, not whichever one enumerated first: a phantom pad at
  // index 0 would otherwise put generic buttons on the hints bar of a real Xbox controller.
  controllerType = detectFromId(activePad ?? firstPad);

  // Everything that is not a direction is its own button and can be held alongside others.
  for (const button of Object.values(BUTTON_INDEX)) {
    if (DIRECTIONS.includes(button)) continue;
    if (down.has(button)) press(button, now);
    else release(button);
  }

  // Directions are one at a time, from whichever of the two is being used. The d-pad is discrete
  // and says exactly what it means, so it wins while it is being touched.
  stickHeld = stickDirection(lx, ly, stickHeld);
  const direction = dpad ?? stickHeld;
  for (const candidate of DIRECTIONS) if (candidate !== direction) release(candidate);
  if (direction) press(direction, now);

  // Right stick scrolls rather than moving focus.
  const scroll = scrollAmount(ry, frameMs);
  if (scroll !== 0) for (const listener of scrollListeners) listener(scroll);

  rafHandle = requestAnimationFrame(poll);
}

/* --------------------------------------------------------------- lifecycle */

export async function startGamepad(): Promise<void> {
  if (started) return;
  started = true;

  if (isDesktop) {
    // gilrs reports presses, repeats and releases, so no polling and no repeat logic here: the
    // native side already applies the same thresholds and the same ramp.
    stopNative = await onNative('gamepad://input', (payload: GamepadInputEvent) => {
      controllerType = payload.controller;
      if (typeof payload.axis === 'number') {
        const scroll = scrollAmount(payload.axis, FRAME_MS);
        if (scroll !== 0) for (const listener of scrollListeners) listener(scroll);
        return;
      }
      const button = payload.button as GamepadButton;
      if (payload.pressed) emit(button, payload.repeat ?? false);
      else emitRelease(button);
    });
    return;
  }

  if (typeof navigator === 'undefined' || !navigator.getGamepads) return;
  // The browser only reports pads after the first interaction with one.
  window.addEventListener('gamepadconnected', () => {
    if (rafHandle === null) rafHandle = requestAnimationFrame(poll);
  });
  rafHandle = requestAnimationFrame(poll);
}

export function stopGamepad(): void {
  started = false;
  stopNative?.();
  stopNative = null;
  if (rafHandle !== null) cancelAnimationFrame(rafHandle);
  rafHandle = null;
  lastPoll = 0;
  releaseAll();
}

/* ---------------------------------------------------- last input source */

export type InputSource = 'pointer' | 'keyboard' | 'gamepad';

let lastSource: InputSource = 'pointer';
const sourceListeners = new Set<(source: InputSource) => void>();

export function lastInputSource(): InputSource {
  return lastSource;
}

export function onInputSourceChange(listener: (source: InputSource) => void): () => void {
  sourceListeners.add(listener);
  return () => sourceListeners.delete(listener);
}

export function noteInputSource(source: InputSource): void {
  if (lastSource === source) return;
  lastSource = source;
  for (const listener of sourceListeners) listener(source);
}

/**
 * Watches what the user last touched.
 *
 * The focus ring and the hints bar only appear for keyboard and gamepad, so a mouse user does
 * not see a focus outline following their clicks (`03-client-app.md` section 3.2).
 */
export function watchInputSource(): () => void {
  const onPointer = () => noteInputSource('pointer');
  const onKey = (e: KeyboardEvent) => {
    if (e.key.startsWith('Arrow') || e.key === 'Tab' || e.key === 'Enter' || e.key === ' ') {
      noteInputSource('keyboard');
    }
  };
  window.addEventListener('pointerdown', onPointer, { passive: true });
  window.addEventListener('mousemove', onPointer, { passive: true });
  window.addEventListener('touchstart', onPointer, { passive: true });
  window.addEventListener('keydown', onKey);
  const offGamepad = onGamepad(() => noteInputSource('gamepad'));

  return () => {
    window.removeEventListener('pointerdown', onPointer);
    window.removeEventListener('mousemove', onPointer);
    window.removeEventListener('touchstart', onPointer);
    window.removeEventListener('keydown', onKey);
    offGamepad();
  };
}

/** The pure parts of the stick feel, exported so the tests can drive them directly. */
export const __stick = { stickDirection, scrollAmount };
